import { create } from 'zustand';
import {
  getAccess,
  getHousehold,
  type AccessResponse,
  type HouseholdMember,
  type HouseholdResponse,
} from '@/lib/api';

// What "covered by a plan" is computed from (lib/coverage.ts): the household's
// plan status and who holds a seat. Fetched once for the whole children area
// rather than per row, and refreshed when the desk mounts or the number of
// linked children changes. A failed fetch leaves the old values; the rows
// then show no coverage chip, never a wrong coverage claim.
interface CoverageState {
  access: AccessResponse | null;
  members: HouseholdMember[] | null;
  /** The whole household, for its plan and seats_over_cap (TUP-1864). */
  household: HouseholdResponse['household'];
  refresh: () => Promise<void>;
  reset: () => void;
}

let inFlight: Promise<void> | null = null;

export const useCoverageStore = create<CoverageState>()((set) => ({
  access: null,
  members: null,
  household: null,

  refresh: () => {
    if (inFlight) return inFlight;
    inFlight = Promise.allSettled([getAccess(), getHousehold()])
      .then(([access, household]) => {
        set({
          ...(access.status === 'fulfilled' ? { access: access.value } : {}),
          ...(household.status === 'fulfilled'
            ? {
                members: household.value.household?.members ?? [],
                household: household.value.household,
              }
            : {}),
        });
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  },

  reset: () => set({ access: null, members: null, household: null }),
}));
